import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, User } from 'lucide-react';
import { testimonials } from '../../data/mock';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  // Image placeholder component
  const ImagePlaceholder = ({ label, image }) => (
    <div className="flex-1">
      {image ? (
        <img 
          src={image} 
          alt={label} 
          className="w-full aspect-[3/4] object-cover rounded-lg"
        />
      ) : (
        <div className="w-full aspect-[3/4] bg-white/5 rounded-lg border border-white/10 flex flex-col items-center justify-center">
          <User className="w-8 h-8 text-white/20 mb-2" />
          <span className="text-white/30 text-xs">Photo</span>
        </div>
      )}
      <p className="text-center text-white/50 text-xs mt-2 font-medium">{label}</p>
    </div>
  );

  return (
    <section className="py-16 sm:py-24 bg-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 uppercase tracking-wider">
            Real Results
          </h2>
          <p className="text-white/50 text-sm sm:text-base max-w-md mx-auto">
            Don't just take my word for it
          </p>
        </div>

        {/* Single Testimonial Card - Mobile Optimized */}
        <div className="max-w-xl mx-auto">
          <div className="section-card relative">
            {/* Quote Icon */}
            <div className="absolute -top-4 left-6 w-8 h-8 bg-white/10 flex items-center justify-center border border-white/30">
              <Quote className="w-4 h-4 text-white" />
            </div>

            <div className="pt-4">
              {/* Before/After Images */}
              <div className="flex gap-3 mb-5">
                <ImagePlaceholder 
                  label="Before" 
                  image={currentTestimonial.beforeImage} 
                />
                <ImagePlaceholder 
                  label="After" 
                  image={currentTestimonial.afterImage} 
                />
              </div>

              {/* Result Badge */}
              <div className="inline-block bg-white/10 text-white text-xs font-bold px-3 py-1.5 mb-3 uppercase tracking-wider">
                {currentTestimonial.result}
              </div>

              {/* Quote */}
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-3">
                "{currentTestimonial.quote}"
              </p>

              {/* Name */}
              <p className="text-white font-semibold text-sm">
                — {currentTestimonial.name}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={goPrev}
              className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-colors border border-white/10"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>

            {/* Dots */}
            <div className="flex gap-1.5">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentIndex(index);
                  }}
                  className={`h-1.5 transition-all ${
                    currentIndex === index
                      ? 'bg-white w-6'
                      : 'bg-white/20 w-1.5 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={goNext}
              className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-colors border border-white/10"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
